/**
 * Equipment CSV import template.
 *
 * Must stay in sync with the backend parser:
 * - `api/src/controllers/equipmentController.ts` (`importEquipmentFromCsv`)
 * - `api/src/services/multerCsvConfig.ts` (`.csv` only, 5 MB max)
 *
 * Format expected by the parser:
 * - Line 1: title line (ignored)
 * - Line 2: header row — must contain `ID_EQUIPEMENT`
 * - Line 3+: data rows, `;`-separated, columns matched BY HEADER NAME
 *   (normalized: case-insensitive, spaces become `_`, e.g. `POSTE TECHNIQUE` = `POSTE_TECHNIQUE`)
 *
 * The 4 legacy "ignored" columns (`N°`, `URL_REDIRECTION`, `ENCODAGE_QR`, `ENCODAGE_NFC`)
 * are no longer part of the template. Legacy 20-column Tereos order files still import:
 * the parser matches by header name, so extra columns are simply not read.
 * `POSTE_TECHNIQUE` and `TAG_SAP` are kept next to each other (SAP pair).
 */

export type CsvColumnRequirement = "required" | "recommended" | "optional";

export type EquipmentCsvColumn = {
  /** 0-based position in the template row. Legacy files may differ: parser uses header names. */
  index: number;
  /** Exact header label expected on line 2. */
  header: string;
  /** i18n suffix: `equipment.importTemplate.fields.<key>.description` */
  key: string;
  requirement: CsvColumnRequirement;
  /** Language-agnostic example value shown in the guide and template. */
  example: string;
};

export const EQUIPMENT_CSV_COLUMNS: EquipmentCsvColumn[] = [
  { index: 0, header: "ID_EQUIPEMENT", key: "idEquipement", requirement: "required", example: "H1G7W4" },
  { index: 1, header: "INFO_SUPPORT", key: "infoSupport", requirement: "optional", example: "Plaque 30*30" },
  { index: 2, header: "SITE", key: "site", requirement: "optional", example: "Bucy-le-Long" },
  { index: 3, header: "BATIMENT", key: "batiment", requirement: "optional", example: "Bâtiment C" },
  { index: 4, header: "REPERE", key: "repere", requirement: "optional", example: "ANCRAGE FOUR A CHAUX" },
  { index: 5, header: "POSTE_TECHNIQUE", key: "posteTechnique", requirement: "optional", example: "1007-SU-25-01.06" },
  { index: 6, header: "TAG_SAP", key: "tagSap", requirement: "optional", example: "IE000000000022000957" },
  { index: 7, header: "DESIGNATION_EQUIPEMENT", key: "designation", requirement: "optional", example: "6 POINTS D'ANCRAGE CORDISTE" },
  { index: 8, header: "TYPE_ELEMENT", key: "typeElement", requirement: "optional", example: "Travail en hauteur" },
  { index: 9, header: "LOCALISATION", key: "localisation", requirement: "optional", example: "-" },
  { index: 10, header: "URL_EALICO", key: "urlEalico", requirement: "optional", example: "https://app.ealico.com/elements/qr_code/..." },
  { index: 11, header: "URL_EALICO_2", key: "urlEalico2", requirement: "optional", example: "" },
  { index: 12, header: "TITRE_LIEN_AUTRE_1", key: "titreLienAutre1", requirement: "optional", example: "" },
  { index: 13, header: "URL_LIEN_AUTRE_1", key: "urlLienAutre1", requirement: "optional", example: "" },
  { index: 14, header: "TITRE_LIEN_AUTRE_2", key: "titreLienAutre2", requirement: "optional", example: "" },
  { index: 15, header: "URL_LIEN_AUTRE_2", key: "urlLienAutre2", requirement: "optional", example: "" }
];

export const EQUIPMENT_CSV_TEMPLATE_FILENAME = "modele-import-equipements.csv";

const TEMPLATE_TITLE = "MODELE IMPORT EQUIPEMENTS - Ligne de titre (ignoree a l'import, ne pas supprimer)";

/** Example rows shipped in the template — placeholder ID must be replaced (unknown IDs are skipped). */
const TEMPLATE_EXAMPLE_ROWS: string[][] = [
  [
    "H1G7W4", "Plaque 30*30", "Bucy-le-Long", "", "ANCRAGE FOUR A CHAUX",
    "1007-SU-25-01.06", "IE000000000022000957",
    "6 POINTS D'ANCRAGE CORDISTE", "Travail en hauteur", "-",
    "https://app.ealico.com/elements/qr_code/2225258a-6b6a-4958-be65-7cd415633d17",
    "", "", "", "", ""
  ],
  // Minimal row: only the ID (and site) are filled in.
  ["XXXXXX", "", "Bucy-le-Long", "", "", "", "", "", "", "", "", "", "", "", "", ""]
];

export const buildEquipmentCsvTemplate = (): string => {
  const columnCount = EQUIPMENT_CSV_COLUMNS.length;
  const pad = (cells: string[]) => [...cells, ...Array(Math.max(0, columnCount - cells.length)).fill("")];

  const lines = [
    pad([TEMPLATE_TITLE]).join(";"),
    EQUIPMENT_CSV_COLUMNS.map((column) => column.header).join(";"),
    ...TEMPLATE_EXAMPLE_ROWS.map((row) => pad(row).join(";"))
  ];

  // BOM so Excel (FR) opens it as UTF-8 with `;` columns; backend reads line 1 as ignored title anyway.
  return `\uFEFF${lines.join("\r\n")}\r\n`;
};

export const downloadEquipmentCsvTemplate = () => {
  const blob = new Blob([buildEquipmentCsvTemplate()], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = EQUIPMENT_CSV_TEMPLATE_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
